import {
  BaseSlotGame,
  ComponentNames,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
  ISlotGameEngineProvider,
  ReelsEngine,
  T_BaseSlotGame,
  T_ISlotGameEngineProvider,
} from '@cgs/machines';
import { ActionActivator, Container, InterpolateCopyAction, Tuple, Vector2 } from '@cgs/wge';

export const SANTA_BOUNCE_ON_STOP_COMPONENT_TOKEN = Symbol('SANTA_BOUNCE_ON_STOP_COMPONENT_TOKEN');

export class SantaBounceOnStopComponent {
  private _engine: ReelsEngine;
  private _baseSlotGame: BaseSlotGame;
  private _activatorsByPos: Map<string, ActionActivator> = new Map();
  private bounceGeneration = 0;

  constructor(container: Container) {
    this._engine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as ReelsEngine;
    this._engine.entityStoped.listen((t: Tuple<number, number>) => {
      const reel = t.item1 as number;
      const line = t.item2 as number;
      this._applyBounce(reel, line);
    });

    this._baseSlotGame = container.resolve<BaseSlotGame>(T_BaseSlotGame);

    // The bounce runs on the scene timeline, outside the reels ECS: an
    // animation that survives into the next spin keeps overwriting the entity
    // position and pins the icon inside the visible area (ghost icons at the
    // bottom row on turbo + autoplay), so every bounce is cancelled as soon as
    // the reels start moving again.
    const gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;
    gameStateMachine.accelerate.entered.listen(() => this.cancelActiveBounces());
  }

  private cancelActiveBounces(): void {
    this.bounceGeneration++;
    for (const activator of this._activatorsByPos.values()) {
      activator.stop();
    }
  }

  private _applyBounce(reel: number, line: number): void {
    let entities;
    try {
      entities = this._engine.getReelStoppedEntities(reel, line, true);
    } catch {
      return;
    }
    if (!entities || entities.length === 0) return;

    const positionIndex = this._engine.entityEngine.getComponentIndex(ComponentNames.Position);
    const iconNodeIndex = this._engine.entityEngine.getComponentIndex(ComponentNames.IconNode);

    const amplitude = Math.max(2, Math.floor(this._engine.ReelConfig.symbolSize.y * 0.05));

    for (const entity of entities) {
      const original = entity.get<Vector2>(positionIndex) as Vector2;
      const up = new Vector2(original.x, original.y - amplitude);

      const iconNode = entity.get<any>(iconNodeIndex);
      if (!iconNode) continue;

      const key = `${reel}:${line}`;
      let activator = this._activatorsByPos.get(key);
      if (!activator) {
        activator = new ActionActivator(this._baseSlotGame);
        this._activatorsByPos.set(key, activator);
      }

      const generation = this.bounceGeneration;
      const upAnim = new InterpolateCopyAction<Vector2>()
        .withInterpolateFunction(Vector2.lerp)
        .withDuration(0.08)
        .withValues(original, up);
      upAnim.valueChange.listen((v: Vector2) => {
        entity.set(positionIndex, v);
      });

      upAnim.done.listen(() => {
        if (generation !== this.bounceGeneration) {
          return;
        }
        const downAnim = new InterpolateCopyAction<Vector2>()
          .withInterpolateFunction(Vector2.lerp)
          .withDuration(0.12)
          .withValues(up, original);
        downAnim.valueChange.listen((v: Vector2) => {
          entity.set(positionIndex, v);
        });
        activator.action = downAnim;
        activator.start();
      });

      activator.action = upAnim;
      activator.start();
    }
  }
}
